import Phaser from "phaser";
import {
  COLORS,
  DIGESTION_LOCKOUT_SPEED,
  PLAYER_IFRAMES_MS,
  RAPID_FIRE_MULT,
  REVIVE_INVULN_MS,
} from "../config";
import type { CellStats, GameMode } from "../types";

/**
 * The white blood cell. Supports BOTH gameplay modes:
 *  • engulf: `strength` grows by eating weaker enemies; digestion lockout; shield.
 *  • shoot:  `power` (damage/shot) + `health` (enemy fire) + rapid fire.
 * The number shown inside the cell is its strength (engulf) or power (shoot).
 */
export default class PlayerCell extends Phaser.Physics.Arcade.Sprite {
  readonly stats: CellStats;
  private mode: GameMode;

  // engulf
  strength: number;
  digesting = false;
  private shieldUntil = 0;
  private engulfGraceUntil = 0;
  // shoot
  power: number;
  health: number;
  private rapidUntil = 0;
  private invulnUntil = 0;

  private label: Phaser.GameObjects.Text;
  private aura?: Phaser.GameObjects.Sprite;

  constructor(scene: Phaser.Scene, x: number, y: number, stats: CellStats, mode: GameMode) {
    super(scene, x, y, "cell");
    this.stats = stats;
    this.mode = mode;
    this.strength = stats.startingStrength;
    this.power = stats.startingPower;
    this.health = stats.maxHealth;

    scene.add.existing(this);
    scene.physics.add.existing(this);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setCircle(stats.radius, this.width / 2 - stats.radius, this.height / 2 - stats.radius);
    body.setCollideWorldBounds(true);
    body.setDamping(true);
    body.setDrag(0.0008, 0.0008);
    this.setDepth(20);

    this.label = scene.add
      .text(x, y, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "30px",
        color: "#ffffff",
        fontStyle: "bold",
        stroke: "#0a1530",
        strokeThickness: 5,
      })
      .setOrigin(0.5)
      .setDepth(21);
  }

  get effectiveRadius(): number {
    return this.stats.radius;
  }

  get currentSpeed(): number {
    return this.stats.moveSpeed * (this.digesting ? DIGESTION_LOCKOUT_SPEED : 1);
  }

  drive(dx: number, dy: number): void {
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.velocity.x = dx * this.currentSpeed;
    body.velocity.y = dy * this.currentSpeed;
  }

  private pop(): void {
    this.scene.tweens.add({ targets: this.label, scale: { from: 1.3, to: 1 }, duration: 160, ease: "Back.easeOut" });
  }

  // ── Engulf mode ──────────────────────────────────────────────────────────────
  canEngulf(): boolean {
    return !this.digesting && this.active && this.scene.time.now >= this.engulfGraceUntil;
  }
  win(gain: number): void {
    this.strength += gain;
    this.pop();
  }
  addStrength(amount: number): void {
    this.strength += amount;
    this.pop();
  }
  beginDigesting(): void {
    this.digesting = true;
  }
  endDigesting(): void {
    this.digesting = false;
  }
  startEngulfGrace(ms: number): void {
    this.engulfGraceUntil = this.scene.time.now + ms;
  }
  activateShield(now: number, ms: number): void {
    this.shieldUntil = now + ms;
  }
  hasShield(now: number): boolean {
    return now < this.shieldUntil;
  }
  consumeShield(): void {
    this.shieldUntil = 0;
  }
  shieldSeconds(now: number): number {
    return Math.max(0, (this.shieldUntil - now) / 1000);
  }
  reviveToStart(): void {
    this.strength = this.stats.startingStrength;
    this.digesting = false;
    this.shieldUntil = 0;
    this.clearTint();
  }

  // ── Shoot mode ───────────────────────────────────────────────────────────────
  addPower(amount: number): void {
    this.power += amount;
    this.pop();
  }
  takeDamage(amount: number, now: number): number {
    if (now < this.invulnUntil || this.health <= 0) return 0;
    this.invulnUntil = now + PLAYER_IFRAMES_MS;
    this.health = Math.max(0, this.health - amount);
    this.setTint(COLORS.enemyShot);
    this.scene.time.delayedCall(120, () => this.clearTint());
    return amount;
  }
  reviveFull(now: number): void {
    this.health = this.stats.maxHealth;
    this.invulnUntil = now + REVIVE_INVULN_MS;
    this.clearTint();
  }
  activateRapidFire(now: number, ms: number): void {
    this.rapidUntil = now + ms;
  }
  fireRateMultiplier(now: number): number {
    return now < this.rapidUntil ? RAPID_FIRE_MULT : 1;
  }
  rapidSeconds(now: number): number {
    return Math.max(0, (this.rapidUntil - now) / 1000);
  }

  preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    this.label.setPosition(this.x, this.y);
    this.label.setText(`${this.mode === "shoot" ? this.power : this.strength}`);

    // Invulnerability blink (shoot).
    this.setAlpha(this.mode === "shoot" && time < this.invulnUntil ? 0.45 + Math.sin(time / 60) * 0.35 : 1);

    // Aura: shield (engulf) or rapid fire (shoot).
    const auraActive =
      this.mode === "engulf" ? time < this.shieldUntil : time < this.rapidUntil;
    if (auraActive && !this.aura) {
      this.aura = this.scene.add
        .sprite(this.x, this.y, "soft")
        .setTint(this.mode === "engulf" ? COLORS.shield : COLORS.rapid)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(19);
    }
    if (this.aura) {
      if (!auraActive) {
        this.aura.destroy();
        this.aura = undefined;
      } else {
        const pulse = 1 + Math.sin(time / 120) * 0.07;
        this.aura.setPosition(this.x, this.y);
        this.aura.setScale((this.stats.radius * 2.7 * pulse) / this.aura.width);
        this.aura.setAlpha(0.3);
      }
    }
  }

  destroy(fromScene?: boolean): void {
    this.label?.destroy();
    this.aura?.destroy();
    super.destroy(fromScene);
  }
}
